<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                    {{ $tc('LBL_RETAILER', 2) }}
                </h1>
                <!-- Breadcrumb -->
                <nav
                    class="text-sm text-gray-500 mb-6"
                    aria-label="Breadcrumb"
                >
                    <ol class="flex items-center space-x-2">
                        <li class="flex items-center">
                            <inertia-link
                                :href="route('landing')"
                                class="text-green-600 hover:underline"
                            >
                                {{ $t('LBL_HOME') }}
                            </inertia-link>
                            <svg
                                class="w-3 h-3 mx-2 text-green-400"
                                fill="currentColor"
                                viewBox="0 0 320 512"
                            >
                                <path
                                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                                />
                            </svg>
                        </li>
                        <li class="text-gray-700">
                            {{ $tc('LBL_USER', 1) }}
                        </li>
                    </ol>
                </nav>
            </div>
            <!-- breadcrumb end -->

            <div
                class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-overflow"
            >
                <!-- Search & Action Bar -->
                <div class="flex flex-wrap items-center justify-between gap-4 p-4 sm:flex-nowrap">
                    <!-- Search Box -->
                    <div class="relative w-full sm:w-auto">
                        <label
                            for="table-search"
                            class="sr-only"
                            >Search</label
                        >
                        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg
                                class="w-5 h-5 text-gray-500 dark:text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                        <t-input
                            id="retailer-search-box"
                            v-model="search"
                            type="text"
                            variant="success"
                            class="w-full sm:w-80 pl-10 pr-4 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            placeholder="Search for retailer"
                            @keypress.enter="fetchPageData()"
                        />
                    </div>

                    <!-- Action Buttons -->
                    <div class="flex flex-wrap gap-2">
                        <t-button
                            v-if="isWholesaler"
                            class="inline-flex items-center rounded-md"
                            variant="success"
                            :disabled="disabled"
                            @click="newTab('wholesaler.statement')"
                        >
                            <span>Statements</span>
                        </t-button>

                        <t-button
                            v-if="isAdmin || isWholesaler"
                            class="inline-flex items-center rounded-md"
                            variant="success"
                            @click="goto('kyc.create')"
                        >
                            Add Retailer
                        </t-button>
                    </div>
                </div>

                <div
                    class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
                >
                    <div class="overflow-x-auto">
                        <table
                            class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                        >
                            <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                                <tr>
                                    <th class="px-4 py-3">#</th>
                                    <th class="px-4 py-3">NAME</th>
                                    <th class="px-4 py-3">INFO</th>
                                    <th class="px-4 py-3">ADDRESS</th>
                                    <th class="px-4 py-3">SALESPERSON</th>
                                    <th class="px-4 py-3">ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="(row, rowIndex) in users.data">
                                    <tr
                                        :class="[
                                            'border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-200',
                                        ]"
                                    >
                                        <td class="px-4 py-3">{{ rowIndex + 1 + (currentPage - 1) * perPage }}</td>
                                        <td class="px-4 py-3">
                                            {{ row.name }}
                                            <div v-if="row.bills_count">
                                                <ShieldIcon
                                                    fill="#5E22C5"
                                                    width="20"
                                                />
                                            </div>
                                        </td>
                                        <td class="px-4 py-3">
                                            <ul class="list-disc marker:text-green-400">
                                                <li><strong>Contact:</strong> {{ row.kyc.person_name }}</li>
                                                <li><strong>Email:</strong> {{ row.kyc.email }}</li>
                                                <li><strong>Phone:</strong> {{ row.kyc.phone }}</li>
                                            </ul>
                                        </td>
                                        <td class="px-4 py-3">{{ row.kyc.business_address }}</td>
                                        <td class="px-4 py-3">
                                            {{
                                                row.inverse_associated_users.length &&
                                                row.inverse_associated_users.filter(
                                                    (user) => user.wholesaler_created == true,
                                                ).length
                                                    ? row.inverse_associated_users.filter(
                                                          (user) => user.wholesaler_created == true,
                                                      )[0].name
                                                    : '--'
                                            }}
                                        </td>
                                        <td class="px-4 py-3 relative">
                                            <div class="inline-flex shadow-sm rounded-md overflow-visible">
                                                <!-- Primary Action -->
                                                <div v-if="row.id === $page.props.user.id">
                                                    <span
                                                        class="rounded-md border border-green-300 bg-green-50 overflow-hidden text-green-600 text-sm px-6 py-1"
                                                    >
                                                        Self
                                                    </span>
                                                </div>

                                                <div v-else-if="!row.deleted_at">
                                                    <t-button
                                                        variant="group"
                                                        class="rounded-none rounded-l-md"
                                                        size="sm"
                                                        @click="suspendUser(row)"
                                                    >
                                                        Suspend
                                                    </t-button>
                                                </div>

                                                <div v-else>
                                                    <t-button
                                                        variant="error"
                                                        size="sm"
                                                        class="rounded-none rounded-l-md px-4 py-2"
                                                        @click="unsuspendUser(row)"
                                                    >
                                                        Activate
                                                    </t-button>
                                                </div>

                                                <!-- Dropdown Toggle Button -->
                                                <!-- <div v-if="!row.deleted_at" class="relative dropdown-wrapper"
                                                    :ref="'dropdown-container-' + row.id">
                                                    <t-button variant="groupoutline" size="sm"
                                                        class="border rounded-none rounded-r-md py-2 w-10"
                                                        @click.stop="toggleDropdown(row.id)">
                                                        . . .
                                                    </t-button> -->

                                                <t-button
                                                    v-if="!row.deleted_at"
                                                    variant="groupoutline"
                                                    size="sm"
                                                    class="border rounded-r-md py-2 w-10"
                                                    @click.stop="toggleDropdown(row.id, $event)"
                                                >
                                                    . . .
                                                </t-button>

                                                <!-- Dropdown Menu -->
                                                <!-- Portal for Dropdown -->
                                                <portal
                                                    to="dropdown-target"
                                                    v-if="activeDropdownId === row.id"
                                                >
                                                    <div
                                                        v-if="activeDropdownId === row.id"
                                                        class="absolute right-0 mt-2 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50"
                                                        :style="dropdownPosition"
                                                    >
                                                        <ul class="text-sm text-gray-700 dark:text-gray-200">
                                                            <li>
                                                                <t-button
                                                                    @click="viewKyc(row)"
                                                                    variant="outline"
                                                                    class="block w-full px-4 py-2 border-none hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                                                                >
                                                                    View
                                                                </t-button>
                                                            </li>
                                                            <li>
                                                                <t-button
                                                                    @click="
                                                                        newTab('retailer.statement', {
                                                                            retailerId: row.id,
                                                                        })
                                                                    "
                                                                    variant="outline"
                                                                    class="block w-full px-4 py-2 border-none hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                                                                >
                                                                    Statement
                                                                </t-button>
                                                            </li>
                                                            <li>
                                                                <t-button
                                                                    @click="
                                                                        goto('reports.retailer', { retailerId: row.id })
                                                                    "
                                                                    variant="outline"
                                                                    class="block w-full px-4 py-2 border-none hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                                                                >
                                                                    Invoice Report
                                                                </t-button>
                                                            </li>
                                                            <li v-if="isWholesaler">
                                                                <t-button
                                                                    @click="assignCredit(row)"
                                                                    variant="outline"
                                                                    class="block w-full px-4 py-2 border-none hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                                                                >
                                                                    Assign Credit
                                                                </t-button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </portal>
                                                <!-- </div> -->
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                                <tr
                                    v-if="!users.data.length"
                                    class="text-center"
                                >
                                    <td
                                        colspan="6"
                                        class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                    >
                                        {{ $t('LBL_DATA_FOUND') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div
                        v-if="users.data.length"
                        class="flex justify-center items-center bg-gray-100 dark:bg-gray-800 px-4 py-4 border-t dark:border-gray-700"
                    >
                        <t-pagination
                            v-model="currentPage"
                            variant="rounded"
                            :total-items="totalRows"
                            :per-page="perPage"
                            :limit="limit"
                            :disabled="!users.data.length"
                            @change="fetchPageData"
                        />
                    </div>
                    <p class="sm:hidden text-xs text-center text-gray-500 py-2">
                        ↔ Swipe left/right to scroll the table
                    </p>
                </div>
            </div>
            <portal-target
                name="dropdown-target"
                multiple
            ></portal-target>
        </div>
    </AppLayout>
</template>

<script>
    import _ from 'lodash';
    import AppLayout from '@/Layouts/AppLayout.vue';
    import ShieldIcon from '@/Components/Icons/ShieldIcon.vue';

    export default {
        name: 'RetailerUserManagement',

        components: { AppLayout, ShieldIcon },

        props: {
            users: {
                type: Object,
                required: true,
            },
            appliedFilters: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                currentPage: null,
                limit: 5,
                perPage: this.users.per_page,
                totalRows: this.users.total,
                search: null,
                mounted: false,
                disabled: false,
                activeDropdownId: null,
                dropdownPosition: {
                    top: '0px',
                    right: '0px',
                },
            };
        },

        watch: {
            search() {
                if (this.mounted) {
                    this.debouncedFetchPageData();
                }
            },
        },

        created() {
            this.currentPage = this.users.current_page;
        },

        beforeDestroy() {
            document.removeEventListener('click', this.handleClickOutside);
        },

        mounted() {
            this.disabled = this.users.data.length === 0;

            if (this.appliedFilters.keyword) {
                this.search = this.appliedFilters.keyword;
            }

            this.debouncedFetchPageData = _.debounce(this.fetchPageData, 2000);

            // ✅ Close dropdown on outside click
            document.addEventListener('click', this.handleClickOutside);

            this.$nextTick(() => {
                this.mounted = true;
            });
        },

        methods: {
            toggleDropdown(userId, event) {
                if (this.activeDropdownId === userId) {
                    this.activeDropdownId = null;
                    return;
                }

                const trigger = event.currentTarget.getBoundingClientRect();
                const scrollY = window.scrollY || window.pageYOffset;
                const scrollX = window.scrollX || window.pageXOffset;

                // Assume dropdown width is 176px (w-44) = 11rem
                const dropdownWidth = 176;
                this.dropdownPosition = {
                    top: `${trigger.bottom + scrollY}px`,
                    left: `${trigger.right + scrollX - dropdownWidth}px`,
                };

                this.activeDropdownId = userId;
            },

            handleClickOutside(event) {
                // Close dropdown if the click is outside any open dropdown
                if (!event.target.closest('.dropdown-wrapper')) {
                    this.activeDropdownId = null;
                }
            },

            suspendUser(user) {
                this.$dialog
                    .confirm({
                        title: 'Confirm',
                        text: 'Are you sure you want to suspend this user?',
                    })
                    .then((result) => {
                        if (result.isOk) {
                            this.$inertia.delete(route('users.management.admin.suspend', user.id));
                        }
                    });
            },

            unsuspendUser(user) {
                this.$dialog
                    .confirm({
                        title: 'Confirm',
                        text: 'Are you sure you want to activate the user with name "' + user.name + '"?',
                        html: true,
                        persistent: true, // optional: prevents closing on outside click
                    })
                    .then((result) => {
                        if (result.isOk) {
                            this.$inertia.post(route('users.management.admin.unsuspend', user.id));
                        }
                    });
            },

            viewKyc(user) {
                this.$inertia.get(route('kyc.show', user.kyc_id));
            },

            viewStatement(user) {
                this.$inertia.get(route('retailer.statement', { retailerId: user.id }));
            },

            assignCredit(user) {
                console.log(user);
                this.$inertia.get(route('credits.assignCredit'), user);
            },

            fetchPageData(newPage) {
                this.$inertia.get(
                    route('users.management.retailer.index', {
                        page: newPage,
                        keyword: this.search,
                    }),
                );
            },
        },
    };
</script>
